import { memo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Play } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { PriceDisplay } from '@/components/public/PriceDisplay';
import { VideoPreviewModal } from '@/components/public/VideoPreviewModal';

interface CatalogCardProps {
  href: string;
  imageUrl?: string;
  imageAlt: string;
  title: string;
  ctaLabel: string;
  isFree?: boolean;
  originalPrice?: string;
  videoUrl?: string;
}

export const CatalogCard = memo(({
  href,
  imageUrl,
  imageAlt,
  title,
  ctaLabel,
  isFree,
  originalPrice,
  videoUrl,
}: CatalogCardProps) => {
  const navigate = useNavigate();
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const hasVideo = Boolean(videoUrl);

  const openCard = () => {
    if (hasVideo) setIsVideoOpen(true);
    else navigate(href);
  };

  return (
    <>
      <Card hover className="catalog-card group h-full overflow-hidden rounded-lg border-slate-200 bg-white">
        <div
          className="flex h-full cursor-pointer flex-col"
          role="button"
          tabIndex={0}
          aria-label={hasVideo ? `Play video preview for ${title}` : `View ${title}`}
          onClick={openCard}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openCard();
            }
          }}
        >
          <div className="catalog-card-media relative aspect-video shrink-0 overflow-hidden border-b border-slate-200 bg-slate-100">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={imageAlt}
                className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
                loading="lazy"
              />
            ) : null}
            {hasVideo ? (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-slate-950/35 opacity-100 transition duration-200 sm:opacity-0 sm:group-hover:opacity-100">
                <span className="flex h-14 w-14 items-center justify-center rounded-full border border-white/70 bg-white/95 text-slate-950 shadow-xl">
                  <Play className="ml-0.5 h-6 w-6 fill-current" />
                </span>
              </div>
            ) : null}
          </div>

          <div className="flex flex-1 flex-col gap-2 p-3">
            <h2 className="line-clamp-2 min-h-[2.2rem] text-sm font-bold leading-[1.1rem] text-slate-950">{title}</h2>

            <div className="mt-auto pt-1">
              <Link
                to={href}
                className="catalog-card-action inline-flex min-h-8 w-full items-center justify-between rounded-md border border-slate-300 px-2.5 text-[0.68rem] font-bold text-slate-950 transition duration-200 group-hover:border-[rgb(var(--accent-solid))]"
                onClick={(event) => event.stopPropagation()}
              >
                <PriceDisplay isFree={isFree} originalPrice={originalPrice} />
                <span className="ml-auto inline-flex items-center gap-1.5">
                  {ctaLabel}
                  <ArrowRight className="h-4 w-4 text-[rgb(var(--accent-solid))] transition duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </Card>

      <VideoPreviewModal
        isOpen={isVideoOpen}
        title={title}
        videoUrl={videoUrl || ''}
        posterUrl={imageUrl}
        detailHref={href}
        detailLabel={ctaLabel}
        onClose={() => setIsVideoOpen(false)}
      />
    </>
  );
});
